import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  applySmokerDeletionPlan,
  createSmoker,
  orderSmokers,
  planSmokerDeletion,
  setDefaultSmoker,
  updateSmoker
} from './smoker.js';

export const SMOKER_ENTITY_TYPE='smoker';

export class SmokerConflictError extends Error{
  constructor(id){
    super('SMOKER_CONFLICT');
    this.name='SmokerConflictError';
    this.smokerId=id;
  }
}

function requireDependencies(repository,queue){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue with enqueue/markConflict is required');
}

function withExistingMeta(existing,domain){
  const meta={...existing};
  delete meta.key;
  delete meta.entityType;
  return {...meta,...domain,id:domain.id};
}

function changed(left,right){
  return JSON.stringify(left)!==JSON.stringify(right);
}

export function createSmokerStore({repository,queue,now=()=>new Date().toISOString()}={}){
  requireDependencies(repository,queue);

  async function get(id){
    return repository.get(SMOKER_ENTITY_TYPE,String(id||'').trim());
  }

  async function list({includeArchived=false,userId=null}={}){
    const rows=await repository.list(SMOKER_ENTITY_TYPE);
    const filtered=(rows||[])
      .filter(row=>!userId||row.userId===userId)
      .filter(row=>includeArchived||!row.archivedAt);
    return orderSmokers(filtered,{includeArchived:true});
  }

  async function create(input){
    const existing=await list({includeArchived:true,userId:input?.userId});
    const active=existing.filter(smoker=>!smoker.archivedAt);
    const shouldDefault=input?.isDefault===true||active.length===0;

    if(shouldDefault){
      for(const smoker of existing){
        if(!smoker.archivedAt&&smoker.isDefault){
          const next=updateSmoker(smoker,{isDefault:false});
          const stored=await get(smoker.id);
          await queue.enqueue(SMOKER_ENTITY_TYPE,withExistingMeta(stored||smoker,next),'update');
        }
      }
    }

    const domain=createSmoker({...input,isDefault:shouldDefault});
    const entity={
      ...createEntityMeta({id:domain.id,now:now(),syncStatus:SyncStatus.PENDING_CREATE}),
      ...domain
    };
    return queue.enqueue(SMOKER_ENTITY_TYPE,entity,'create');
  }

  async function edit(id,changes){
    const current=await get(id);
    if(!current)throw new TypeError('Smoker not found');
    if(current.syncStatus===SyncStatus.CONFLICT)throw new SmokerConflictError(current.id);
    const domain=updateSmoker(current,changes);
    return queue.enqueue(SMOKER_ENTITY_TYPE,withExistingMeta(current,domain),'update');
  }

  async function setDefault(id){
    const current=await get(id);
    if(!current)throw new TypeError('Smoker not found');
    const smokers=await list({includeArchived:true,userId:current.userId});
    const next=setDefaultSmoker(smokers,id);
    const changedRows=[];
    for(const smoker of next){
      const existing=await get(smoker.id);
      if(existing&&existing.isDefault!==smoker.isDefault){
        changedRows.push(await queue.enqueue(
          SMOKER_ENTITY_TYPE,
          withExistingMeta(existing,smoker),
          'update'
        ));
      }
    }
    return Object.freeze(changedRows);
  }

  async function planArchive(id,{historicalSessionCount=0}={}){
    const current=await get(id);
    if(!current)throw new TypeError('Smoker not found');
    const smokers=await list({includeArchived:true,userId:current.userId});
    return planSmokerDeletion(smokers,id,{historicalSessionCount});
  }

  async function archive(id,{historicalSessionCount=0,replacementDefaultId=null}={}){
    const current=await get(id);
    if(!current)throw new TypeError('Smoker not found');
    const smokers=await list({includeArchived:true,userId:current.userId});
    const plan=planSmokerDeletion(smokers,id,{historicalSessionCount});

    if(plan.requiresReplacementSelection&&!replacementDefaultId){
      return Object.freeze({ok:false,status:'replacement_required',plan});
    }

    const next=applySmokerDeletionPlan(smokers,plan,{
      now:now(),
      replacementDefaultId:replacementDefaultId||plan.replacementDefaultId
    });

    const updated=[];
    for(const smoker of next){
      const existing=await get(smoker.id);
      if(existing&&changed(
        {archivedAt:existing.archivedAt,isDefault:existing.isDefault},
        {archivedAt:smoker.archivedAt,isDefault:smoker.isDefault}
      )){
        updated.push(await queue.enqueue(
          SMOKER_ENTITY_TYPE,
          withExistingMeta(existing,smoker),
          'update'
        ));
      }
    }

    return Object.freeze({ok:true,status:'archived',plan,updated:Object.freeze(updated)});
  }

  async function markConflict(id,conflict){
    const current=await get(id);
    if(!current)throw new TypeError('Smoker not found');
    return queue.markConflict(SMOKER_ENTITY_TYPE,current,conflict);
  }

  return Object.freeze({get,list,create,edit,setDefault,planArchive,archive,markConflict});
}
